/**
 * Sidecar：团队 / 工作板 / 要你决定 / 证据共用的那一条右侧轨道。
 *
 * # 它是布局轨道，不是模态
 *
 * 设计稿把这一条单独立了一节，因为上一版就是做错的：那时它是带遮罩的覆盖层，
 * 于是**每次看工作板都要把聊天挡掉**。现在的规则是向右多开一条轨道，
 * 聊天列只是变窄，不被遮。
 *
 * 落到实现上就一句话：它是 flex 主轴上的第三个 track，不是 `position: fixed`。
 * 用了 fixed 就必然违反「不覆盖消息、输入框、滚动条」那一条——
 * 这不是风格选择，fixed 元素本来就脱离文档流，它没法「让位」。
 *
 * # 为什么折叠顺序要写成一个函数，而不是几条 CSS 媒体查询
 *
 * 顺序本身是产品决定：**先牺牲会话栏，再收 Sidecar，最后才动聊天列，
 * 永远不盖住聊天**。媒体查询能实现它，但实现之后没有任何东西能验证它——
 * 而这条顺序恰好是验收 §D.24 与 §I.58 逐条盯的东西。
 *
 * 写成纯函数，它就能被用例按宽度逐档钉住。
 */

export const SIDECAR_TABS = ['team', 'board', 'decisions', 'evidence'] as const
export type SidecarTab = (typeof SIDECAR_TABS)[number]

/** 子 Agent 运行面板 v3：默认 420px，可拖 380–520px。 */
export const SIDECAR_DEFAULT_WIDTH = 420
export const SIDECAR_MIN_WIDTH = 380
export const SIDECAR_MAX_WIDTH = 520

/** 会话栏：完整 236px，图标条 52px。设计稿那句「236px → 52px」就是这两个数。 */
export const RAIL_FULL_WIDTH = 236
export const RAIL_ICON_WIDTH = 52

/**
 * 聊天列的舒适宽度。低于它就开始按顺序让位。
 *
 * 刻意叫「舒适」而不是「最小」：SPEC-components 写的是 640，
 * 而设计稿自己的 1024 断点那一屏算出来是 612（52 + 612 + 360 = 1024）。
 * 两个数都是设计稿给的，说明 640 是想要的宽度、不是硬下限。
 * 把它当硬下限会推出一个设计稿自己都不满足的布局。
 */
export const CHAT_COMFORT_WIDTH = 640

/** 低于这个宽度，会话栏收进按钮触发的覆盖层。Sidecar 仍然是轨道。 */
export const RAIL_OVERLAY_BELOW = 1024

/**
 * 低于这个宽度就是手机。与外壳自己的断点同一个数（它用 `max-[600px]`），
 * 两处对不上的话会出现「外壳已经切成手机版、而这一屏还在按桌面排」的一档。
 */
export const MOBILE_BELOW = 601

export type RailMode =
  /** 236px 完整会话栏。 */
  | 'full'
  /** 52px 图标条。会话还能点，只是不显示标题。 */
  | 'icons'
  /** 收进按钮触发的覆盖层。只在 1024 以下。 */
  | 'overlay'

/**
 * Sidecar 在这个宽度下是什么形态。
 *
 * `track` 是桌面那条并排的轨道；`sheet` 是手机上的全屏层。
 *
 * **手机上刻意不是「一条更窄的轨道」。** 390px 的屏摆不下两列——
 * 硬摆的结果是聊天列被挤出屏幕，而那比「盖住聊天」更糟：盖住至少还看得见
 * 有东西盖在上面，挤出去是直接消失。设计稿把这一条写成了验收项
 * （§D.26：移动端打开的是全屏层或 bottom sheet，不是缩小的 Sidecar）。
 *
 * 注意这与桌面那条「绝不覆盖聊天」不矛盾，两者是同一个目标在两种空间下的
 * 做法：**让用户始终能回到聊天**。桌面上空间够，所以并排；手机上空间不够，
 * 所以全屏层配一个明确的返回，而不是把两件事挤在一起谁都看不清。
 */
export type SidecarMode = 'track' | 'sheet'

export interface ResolvedTracks {
  /**
   * 这一档是不是手机。
   *
   * **它是一个名字，不是一个近似量。** 2026-09-07 之前，「是不是窄屏」
   * 由调用方拿 `railMode === 'overlay'` 加上「会话导航在不在外面」拼出来，
   * 而 `cc1d45c93` 把会话导航搬进全局侧栏之后，那个拼法**恒为 false**——
   * 手机上的底部 sheet、窄屏脚注、力度 sheet 一起静默失效，
   * 而没有任何东西会红：布局照样能渲染，只是形状退回了下拉。
   *
   * `railMode === 'overlay'` 今天也不能代表手机：宽屏把会话栏交给外壳时
   * 同样是 overlay。所以这里给它一个自己的字段。
   */
  readonly mobile: boolean
  readonly railMode: RailMode
  /** 会话栏在主轴上占的宽度。overlay 时是 0——它不再占位。 */
  readonly railWidth: number
  readonly sidecarMode: SidecarMode
  /** Sidecar 在主轴上占的宽度。sheet 形态时是 0——它浮在上面，不占位。 */
  readonly sidecarWidth: number
  readonly chatWidth: number
}

export function clampSidecarWidth(width: number): number {
  if (!Number.isFinite(width)) return SIDECAR_DEFAULT_WIDTH
  return Math.max(SIDECAR_MIN_WIDTH, Math.min(SIDECAR_MAX_WIDTH, Math.round(width)))
}

export function isSidecarTab(value: unknown): value is SidecarTab {
  return typeof value === 'string' && (SIDECAR_TABS as readonly string[]).includes(value)
}

/**
 * 从 URL query 读页签。
 *
 * 设计稿要求 Sidecar 状态入 URL，刷新后保持。认不出的值当成没开——
 * 不猜一个默认页签：`?sidecar=teem` 打错字时开一个「当前团队」，
 * 会让人以为自己拼对了。
 */
export function parseSidecarTab(raw: unknown): SidecarTab | null {
  return isSidecarTab(raw) ? raw : null
}

/**
 * 按可用宽度解出三条轨道。
 *
 * 让位顺序（设计稿 W08b，逐字）：
 *   ① 会话栏 236 → 52
 *   ② Sidecar 收窄到 380
 *   ③ 聊天列变窄
 *   永远不做：盖住聊天
 *
 * 第 ③ 步没有下限，因为**聊天列变窄总比被盖住好**——设计稿把这一条单独标红。
 * 窄到不好用是个体验问题，被盖住是个诚信问题：用户会以为消息没了。
 */
export function resolveTracks(
  viewportWidth: number,
  sidecarOpen: boolean,
  requestedSidecarWidth: number = SIDECAR_DEFAULT_WIDTH,
  conversationRail = true,
): ResolvedTracks {
  const sidecar = sidecarOpen ? clampSidecarWidth(requestedSidecarWidth) : 0

  // 手机：Sidecar 不占主轴，聊天列拿走整屏。
  // 这一档必须排在下面所有判断之前——它不是「更窄的桌面」，是另一种布局。
  if (viewportWidth < MOBILE_BELOW) {
    return {
      mobile: true,
      railMode: 'overlay',
      railWidth: 0,
      sidecarMode: 'sheet',
      sidecarWidth: 0,
      chatWidth: viewportWidth,
    }
  }

  // 全局外壳已经接管会话导航时，这一屏只解聊天与 Sidecar 两条轨道。
  // 不在这里继续减旧会话栏的 236px，否则 DOM 虽然只有一栏，聊天区却仍会
  // 留出一块看不见的空位。
  if (!conversationRail) {
    // **挤不下时也要把 Sidecar 收到最窄**，与下面第 ③ 步同一条规则。
    //
    // 2026-08-30 之前这里是直接 return 的，于是那一步整个够不着：
    // 会话栏搬进全局侧栏之后，这一屏的可用宽度少了侧栏那一截
    // （useAvailableWidth 量的是容器，不是窗口），而 1024 那一档
    // 算出来的聊天列是 540——低于 CHAT_COMFORT_WIDTH，
    // 而本来该把 Sidecar 收到 380 换回 580 的那段代码在早返回后面。
    //
    // 表现是屏幕上聊天列被挤窄，两侧都不报错。
    // e2e `lulu.spec.ts` 折叠档那条逮到的就是它——**它期望的 380
    // 一直是对的，错的是这段实现**。
    const roomy = viewportWidth - sidecar
    if (roomy >= CHAT_COMFORT_WIDTH || !sidecarOpen) {
      return {
        mobile: false,
        railMode: 'overlay',
        railWidth: 0,
        sidecarMode: 'track',
        sidecarWidth: sidecar,
        chatWidth: Math.max(0, roomy),
      }
    }
    // 收窄只往窄里收：本来就比最窄还窄的（用户拖过），不许被拉宽。
    const narrow = Math.min(sidecar, SIDECAR_MIN_WIDTH)
    return {
      mobile: false,
      railMode: 'overlay',
      railWidth: 0,
      sidecarMode: 'track',
      sidecarWidth: narrow,
      chatWidth: Math.max(0, viewportWidth - narrow),
    }
  }

  // 1024 以下会话栏离开主轴。这一档由宽度直接决定，与 Sidecar 开不开无关：
  // 一个 900px 的窗口摆不下 236px 的会话栏，不管右边有没有东西。
  if (viewportWidth < RAIL_OVERLAY_BELOW) {
    return {
      mobile: false,
      railMode: 'overlay',
      railWidth: 0,
      sidecarMode: 'track',
      sidecarWidth: sidecar,
      chatWidth: Math.max(0, viewportWidth - sidecar),
    }
  }

  // ① 先试完整会话栏。
  const withFullRail = viewportWidth - RAIL_FULL_WIDTH - sidecar
  if (withFullRail >= CHAT_COMFORT_WIDTH) {
    return {
      mobile: false,
      railMode: 'full',
      railWidth: RAIL_FULL_WIDTH,
      sidecarMode: 'track',
      sidecarWidth: sidecar,
      chatWidth: withFullRail,
    }
  }

  // ② 会话栏收成图标条。
  const withIconRail = viewportWidth - RAIL_ICON_WIDTH - sidecar
  if (withIconRail >= CHAT_COMFORT_WIDTH || !sidecarOpen) {
    return {
      mobile: false,
      railMode: 'icons',
      railWidth: RAIL_ICON_WIDTH,
      sidecarMode: 'track',
      sidecarWidth: sidecar,
      chatWidth: Math.max(0, withIconRail),
    }
  }

  // ③ Sidecar 收到最窄，剩下的全给聊天列。
  const narrowSidecar = SIDECAR_MIN_WIDTH
  return {
    mobile: false,
    railMode: 'icons',
    railWidth: RAIL_ICON_WIDTH,
    sidecarMode: 'track',
    sidecarWidth: narrowSidecar,
    chatWidth: Math.max(0, viewportWidth - RAIL_ICON_WIDTH - narrowSidecar),
  }
}

/**
 * 宽度与会话栏折叠状态按用户持久化。页签不在这里——它在 URL 里。
 *
 * 分两处存是刻意的：页签是「我现在在看哪一页」，属于这一次导航，
 * 应该能被分享和后退；宽度是「我喜欢多宽」，属于这个人，
 * 换一条会话也该保持。把两者存一处，要么宽度进了 URL（分享链接会改别人的布局），
 * 要么页签进了 localStorage（刷新之后回不到原来那一页）。
 */
const STORAGE_KEY = 'npc.lulu.sidecar'

export interface SidecarPreference {
  readonly width: number
  readonly railCollapsed: boolean
}

export const DEFAULT_SIDECAR_PREFERENCE: SidecarPreference = {
  width: SIDECAR_DEFAULT_WIDTH,
  railCollapsed: false,
}

export function readSidecarPreference(storage?: Pick<Storage, 'getItem'>): SidecarPreference {
  const store = storage ?? safeLocalStorage()
  if (!store) return DEFAULT_SIDECAR_PREFERENCE
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SIDECAR_PREFERENCE
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SIDECAR_PREFERENCE
    const record = parsed as Record<string, unknown>
    return {
      width: clampSidecarWidth(
        typeof record.width === 'number' ? record.width : SIDECAR_DEFAULT_WIDTH,
      ),
      railCollapsed: record.railCollapsed === true,
    }
  } catch {
    // 存坏了就用默认值。这里不报错也不清空：用户的布局偏好坏掉不值得打断他，
    // 而清空会让「下次还是坏的」这件事查不出来。
    return DEFAULT_SIDECAR_PREFERENCE
  }
}

export function writeSidecarPreference(
  preference: SidecarPreference,
  storage?: Pick<Storage, 'setItem'>,
): void {
  const store = storage ?? safeLocalStorage()
  if (!store) return
  try {
    store.setItem(
      STORAGE_KEY,
      JSON.stringify({
        width: clampSidecarWidth(preference.width),
        railCollapsed: preference.railCollapsed,
      }),
    )
  } catch {
    // 无痕模式下 setItem 会抛。存不下偏好不该让工作台崩掉。
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}
